<script setup lang="ts">
import { computed } from 'vue';

const { setAttr } = useVisualEditing();
const props = defineProps<{
  data: {
    id: number;
    image?: string | { id: string } | null;
    name?: string | null;
    // Nouveaux champs
    logo1?: string | { id: string } | null;
    logo2?: string | { id: string } | null;
    overlay_color?: string | null; // ex: "#000000" ou "rgba(0,0,0,0.6)"
    // Optionnel : hauteur en vh (par défaut 100)
    height_vh?: number | null;
  };
}>();

// Normalisation d'IDs Directus (string UUID ou { id })
const normalizeId = (val: unknown) => {
  const v: any = val;
  return typeof v === 'string' ? v : (v?.id ?? null);
};
const imageId = computed(() => normalizeId(props.data.image));
const logo1Id = computed(() => normalizeId(props.data.logo1));
const logo2Id = computed(() => normalizeId(props.data.logo2));

// Hauteur du bloc (plein écran par défaut)
const containerHeight = computed(() => `${props.data.height_vh ?? 100}vh`);

// Overlay : on laisse l’opacité à 0.35 par défaut pour un “tint” lisible.
// Si vous fournissez déjà une couleur avec alpha (ex: rgba(..., 0.5)),
// l’opacité s’additionnera. Ajustez ci-dessous si besoin.
const overlayStyle = computed(() => {
  if (!props.data.overlay_color) return undefined;
  return {
    backgroundColor: props.data.overlay_color,
    opacity: '1',
  } as const;
});
const focal = computed(() => {
  // if (props.data.focal && props.data.focal.trim()) return props.data.focal.trim();
  // const clamp = (n: number) => Math.max(0, Math.min(100, n));
  // const x = clamp(props.data.focal_x ?? 50);
  // const y = clamp(props.data.focal_y ?? 50);
  // return `${x}% ${y}%`;
  return '22% 70%';
});
</script>

<template>
  <section
    class="relative left-[calc(50%-50vw+10px)] w-[calc(100vw-2*var(--tapis))] sm:left-[calc(50%-50vw+20px)] md:left-[calc(50%-50vw+30px)]"
  >
    <div class="relative w-full overflow-hidden" :style="{ height: containerHeight }">
      <!-- Image de fond -->
      <div class="absolute inset-0">
        <DirectusImage
          v-if="imageId"
          :uuid="imageId"
          :alt="data.name || 'Image'"
          sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw"
          :preload="true"
          class="h-full w-full object-cover"
          :key="imageId"
          :style="{ objectPosition: focal }"
          :data-directus="
            setAttr({
              collection: 'block_image_logo',
              item: data.id,
              fields: ['image', 'logo1', 'logo2', 'overlay_color'],
              mode: 'modal',
            })
          "
        />
        <!-- fit="cover" -->
        <!-- :placeholder="false" -->
        <div v-else class="flex h-full w-full items-center justify-center bg-neutral-100 text-neutral-500">
          Add an image…
        </div>
      </div>

      <!-- Couleur d’overlay par-dessus toute l’image -->
      <div v-if="overlayStyle" class="absolute inset-0 z-10 pointer-events-none" :style="overlayStyle" />

      <!-- Logos côte à côte, centrés en bas -->
      <div
        v-if="logo1Id || logo2Id"
        class="absolute inset-x-0 top-[40%] -translate-y-1/2 z-20 flex flex-wrap items-center justify-center gap-32 px-6 pointer-events-none"
      >
        <!-- Chaque logo est contenu dans une boîte à hauteur fixe (responsive) -->
        <div v-if="logo1Id" class="relative h-20 sm:h-22 md:h-24 w-auto mx-10">
          <DirectusImage
            :uuid="logo1Id"
            alt="Logo"
            sizes="(min-width: 1024px) 15vw, (min-width: 640px) 25vw, 40vw"
            class="object-contain h-full"
            :key="logo1Id"
          />
        </div>
        <div v-if="logo2Id" class="relative h-24 sm:h-24 md:h-24 w-auto">
          <DirectusImage
            :uuid="logo2Id"
            alt="Le yoga d'Hélène"
            sizes="(min-width: 1024px) 15vw, (min-width: 640px) 25vw, 40vw"
            class="object-contain h-full"
            :key="logo2Id"
          />
        </div>
      </div>
    </div>
  </section>
</template>
